'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { RadialBarChart, RadialBar, ResponsiveContainer } from 'recharts';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface SentimentSource {
  source: string;
  score: number | null;
  signal: string | null;
  articles: number;
}

interface AIForecast {
  symbol: string;
  trend_direction: string | null;
  confidence_score: number | null;
  created_at: string;
}

const SIGNAL_COLORS: Record<string, string> = {
  Bullish: 'text-emerald-400',
  Bearish: 'text-red-400',
  Neutral: 'text-amber-400',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-500',
  processing: 'bg-amber-500',
  idle: 'bg-slate-500',
};

export default function AISentimentPanel() {
  const [sources, setSources] = useState<SentimentSource[]>([]);
  const [overallScore, setOverallScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'sentiment' | 'agents'>('sentiment');
  const [forecasts, setForecasts] = useState<AIForecast[]>([]);
  const [forecastsLoading, setForecastsLoading] = useState(false);
  const supabase = createClient();

  const fetchSentiment = async () => {
    try {
      const res = await fetch('/api/market/sentiment');
      const json = await res.json();
      if (json.sources) {
        setSources(json.sources);
        setOverallScore(json.overallScore);
      }
    } catch (err) {
      // Keep state
    } finally {
      setLoading(false);
    }
  };

  const fetchForecasts = async () => {
    setForecastsLoading(true);
    try {
      const { data } = await supabase
        .from('ai_forecasts')
        .select('symbol, trend_direction, confidence_score, created_at')
        .order('created_at', { ascending: false })
        .limit(10);
      setForecasts(data || []);
    } catch { /* keep state */ } finally {
      setForecastsLoading(false);
    }
  };

  useEffect(() => {
    fetchSentiment();

    const channel = supabase
      .channel('sentiment_data_changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sentiment_data' }, () => {
        fetchSentiment();
      })
      .subscribe();

    const pollId = setInterval(fetchSentiment, 60000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollId);
    };
  }, []);

  useEffect(() => {
    if (tab === 'agents') {
      fetchForecasts();
    }
  }, [tab]);

  const hasData = sources.length > 0 && overallScore != null;
  const gaugeColor = overallScore != null
    ? (overallScore >= 60 ? '#10B981' : overallScore >= 40 ? '#F59E0B' : '#EF4444')
    : '#334155';
  const gaugeData = [{ name: 'Score', value: overallScore ?? 0, fill: gaugeColor }];

  // Derive agent statuses from recent forecast data
  const lastForecastTime = forecasts[0]?.created_at
    ? new Date(forecasts[0].created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : null;
  const hasForecasts = forecasts.length > 0;

  const AGENTS = [
    {
      name: 'Price Forecaster',
      status: hasForecasts ? 'active' : 'idle',
      lastRun: lastForecastTime ? `Last run ${lastForecastTime}` : 'Not yet run',
      accuracy: hasForecasts
        ? Math.round(forecasts.filter(f => f.confidence_score != null).reduce((a, f) => a + (f.confidence_score ?? 0), 0) / Math.max(forecasts.filter(f => f.confidence_score != null).length, 1))
        : null,
    },
    {
      name: 'Sentiment Analyzer',
      status: sources.length > 0 ? 'active' : 'idle',
      lastRun: sources.length > 0 ? 'Live data' : 'Configure NewsAPI',
      accuracy: overallScore,
    },
    {
      name: 'Trading Signal Generator',
      status: hasForecasts ? 'active' : 'idle',
      lastRun: lastForecastTime ? `Last run ${lastForecastTime}` : 'Not yet run',
      accuracy: hasForecasts
        ? Math.round(forecasts.filter(f => f.confidence_score != null && f.confidence_score >= 60).length / Math.max(forecasts.length, 1) * 100)
        : null,
    },
    {
      name: 'Risk Monitor',
      status: 'idle' as const,
      lastRun: 'Passive monitoring',
      accuracy: null,
    },
  ];

  return (
    <div className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#334155] shrink-0">
        <div className="flex items-center gap-2">
          <Icon name="CpuChipIcon" size={16} variant="solid" className="text-sky-500" />
          <span className="text-sm font-semibold text-slate-200 font-mono">AI Intelligence</span>
          {loading && <div className="w-3 h-3 border border-sky-500 border-t-transparent rounded-full animate-spin" />}
        </div>
        <div className="flex rounded overflow-hidden border border-[#334155]">
          <button
            onClick={() => setTab('sentiment')}
            className={`px-2 py-1 text-xs font-mono transition-all ${tab === 'sentiment' ? 'bg-sky-500 text-white' : 'bg-[#334155] text-slate-400 hover:text-slate-200'}`}
          >
            Sentiment
          </button>
          <button
            onClick={() => setTab('agents')}
            className={`px-2 py-1 text-xs font-mono transition-all ${tab === 'agents' ? 'bg-sky-500 text-white' : 'bg-[#334155] text-slate-400 hover:text-slate-200'}`}
          >
            Agents
          </button>
        </div>
      </div>

      {tab === 'sentiment' ? (
        <div className="flex-1 overflow-y-auto">
          <div className="flex items-center gap-4 px-4 py-3 border-b border-[#334155]/50">
            <div className="w-20 h-20 shrink-0" aria-label={`Overall AI sentiment score: ${overallScore ?? 'N/A'}`}>
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" data={gaugeData} startAngle={90} endAngle={-270}>
                  <RadialBar dataKey="value" cornerRadius={4} background={{ fill: '#334155' }} />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <p className="text-xs text-slate-500 font-mono">Overall Sentiment</p>
              {hasData && overallScore != null ? (
                <>
                  <p className={`text-2xl font-mono font-bold ${overallScore >= 60 ? 'text-emerald-400' : overallScore >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                    {overallScore}
                  </p>
                  <p className={`text-xs font-mono ${overallScore >= 60 ? 'text-emerald-400' : overallScore >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                    {overallScore >= 60 ? '▲ Bullish' : overallScore >= 40 ? '→ Neutral' : '▼ Bearish'}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-mono font-bold text-slate-600">—</p>
                  <p className="text-xs font-mono text-slate-600">No data</p>
                </>
              )}
            </div>
          </div>

          {!loading && !hasData && (
            <div className="px-4 py-3 text-center">
              <p className="text-xs text-slate-600 font-mono">Sentiment data unavailable</p>
              <p className="text-xs text-slate-700 font-mono mt-1">Configure NewsAPI key to enable</p>
            </div>
          )}

          <div className="divide-y divide-[#334155]/50">
            {sources.map(s => (
              <div key={s.source} className="flex items-center justify-between px-4 py-2.5 hover:bg-[#334155]/20 transition-all">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-300 truncate">{s.source}</p>
                  {s.articles > 0 && (
                    <p className="text-xs text-slate-600 font-mono">{s.articles.toLocaleString()} signals</p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="w-16 h-1.5 bg-[#334155] rounded-full overflow-hidden">
                    {s.score != null && (
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${s.score >= 60 ? 'bg-emerald-500' : s.score >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${s.score}%` }}
                      />
                    )}
                  </div>
                  <span className="text-xs font-mono text-slate-400 w-6 text-right">
                    {s.score != null ? s.score : '—'}
                  </span>
                  <span className={`text-xs font-mono font-bold w-14 text-right ${s.signal ? (SIGNAL_COLORS[s.signal] || 'text-slate-400') : 'text-slate-600'}`}>
                    {s.signal ?? '—'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {forecastsLoading && (
            <div className="flex items-center justify-center py-6">
              <div className="w-4 h-4 border border-sky-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!forecastsLoading && (
            <>
              <div className="divide-y divide-[#334155]/50">
                {AGENTS.map(agent => (
                  <div key={agent.name} className="flex items-center justify-between px-4 py-3 hover:bg-[#334155]/20 transition-all">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_COLORS[agent.status]} ${agent.status === 'active' ? 'animate-pulse' : ''}`} />
                      <div>
                        <p className="text-xs font-medium text-slate-200">{agent.name}</p>
                        <p className="text-xs text-slate-500 font-mono">{agent.lastRun}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-xs font-mono ${agent.accuracy != null ? (agent.accuracy >= 60 ? 'text-emerald-400' : agent.accuracy >= 40 ? 'text-amber-400' : 'text-red-400') : 'text-slate-600'}`}>
                        {agent.accuracy != null ? `${agent.accuracy}%` : '—'}
                      </p>
                      <p className={`text-xs capitalize font-mono ${agent.status === 'active' ? 'text-emerald-500' : 'text-slate-600'}`}>{agent.status}</p>
                    </div>
                  </div>
                ))}
              </div>

              {hasForecasts && (
                <div className="px-4 py-3 border-t border-[#334155]">
                  <p className="text-xs text-slate-500 font-mono mb-2">Recent Forecasts</p>
                  <div className="space-y-1">
                    {forecasts.slice(0, 5).map((f, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-xs font-mono text-sky-400">{f.symbol}</span>
                        <div className="flex items-center gap-2">
                          {f.trend_direction && (
                            <span className={`text-xs font-mono ${f.trend_direction === 'bullish' ? 'text-emerald-400' : f.trend_direction === 'bearish' ? 'text-red-400' : 'text-amber-400'}`}>
                              {f.trend_direction}
                            </span>
                          )}
                          {f.confidence_score != null && (
                            <span className="text-xs font-mono text-slate-400">{f.confidence_score}%</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          <div className="px-4 py-3 border-t border-[#334155]">
            <Link href="/asset-analysis-screen" className="text-xs text-sky-400 hover:text-sky-300 font-mono flex items-center gap-1 transition-all">
              <Icon name="CpuChipIcon" size={12} variant="outline" />
              Run AI Analysis on Asset
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}